import { combineWithTranscript } from '../lib/gate.ts';
import { PCM_RATE, speechOnsetMs, trimForReplay } from '../lib/pcm.ts';
import { tokenize } from '../lib/text.ts';
import type { AssessmentResult, PhonemeScore, PlainHeard, WordErrorType, WordScore } from '../types.ts';
import { ASSESS_SETTINGS } from './azureSettings.ts';
import { startPcmTap } from './pcmTap.ts';
import { AssessError } from './types.ts';
import type { Assessor } from './types.ts';

// --- parsowanie JSON z Azure ------------------------------------------------------------------

interface AzureWord {
  Word?: string;
  Offset?: number;
  Duration?: number;
  PronunciationAssessment?: { AccuracyScore?: number; ErrorType?: string };
  Phonemes?: { Phoneme?: string; Offset?: number; Duration?: number; PronunciationAssessment?: { AccuracyScore?: number } }[];
}
interface AzureNBest {
  Display?: string;
  Lexical?: string;
  PronunciationAssessment?: {
    AccuracyScore?: number;
    FluencyScore?: number;
    CompletenessScore?: number;
    PronScore?: number;
    ProsodyScore?: number;
  };
  Words?: AzureWord[];
}
interface AzureJson {
  RecognitionStatus?: string;
  DisplayText?: string;
  NBest?: AzureNBest[];
}

/** Czasy w odpowiedzi Azure są w jednostkach po 100 ns; zwracamy ms (z zaokrągleniem) albo nic, gdy pola nie ma. */
const ticksToMs = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v / 10000) : undefined);

const KNOWN_ERRORS: readonly WordErrorType[] = ['None', 'Mispronunciation', 'Omission', 'Insertion'];

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Zamienia surowy JSON z Azure Pronunciation Assessment na nasz AssessmentResult. */
export function parseAzureJson(input: string | object): AssessmentResult {
  const json = (typeof input === 'string' ? JSON.parse(input) : input) as AzureJson;
  const status = json.RecognitionStatus;
  if (status && status !== 'Success') {
    throw new AssessError(status === 'NoMatch' || status === 'InitialSilenceTimeout' ? 'no-speech' : 'network', `RecognitionStatus=${status}`);
  }
  const best = json.NBest?.[0];
  const pa = best?.PronunciationAssessment;
  if (!best || !pa) throw new AssessError('no-speech', 'Brak wyniku oceny w odpowiedzi Azure');

  let capped = false;
  const words: WordScore[] = (best.Words ?? []).map((w) => {
    const raw = w.PronunciationAssessment?.ErrorType ?? 'None';
    const error = (KNOWN_ERRORS as readonly string[]).includes(raw) ? (raw as WordErrorType) : 'None';
    const azureAccuracy = num(w.PronunciationAssessment?.AccuracyScore);
    const score: WordScore = { word: String(w.Word ?? ''), accuracy: azureAccuracy, error };
    const wOff = ticksToMs(w.Offset);
    const wDur = ticksToMs(w.Duration);
    if (wOff !== undefined) score.offsetMs = wOff;
    if (wDur !== undefined) score.durationMs = wDur;
    if (w.Phonemes?.length) {
      score.phonemes = w.Phonemes.map((p) => {
        const ph: PhonemeScore = { phoneme: String(p.Phoneme ?? '?'), accuracy: num(p.PronunciationAssessment?.AccuracyScore) };
        const off = ticksToMs(p.Offset);
        const dur = ticksToMs(p.Duration);
        if (off !== undefined) ph.offsetMs = off;
        if (dur !== undefined) ph.durationMs = dur;
        return ph;
      });
      // Azure potrafi dać wysoką ocenę słowu, w którym większość głosek nie wybrzmiała (np. ucięte "wa" zamiast "water":
      // słowo 73, ale głoski 100/93/31/0/19). Ocena słowa nie może być wyższa niż zwykła średnia jego głosek.
      const mean = Math.round(score.phonemes.reduce((a, p) => a + p.accuracy, 0) / score.phonemes.length);
      if (mean < azureAccuracy) {
        score.accuracy = mean;
        score.azureAccuracy = azureAccuracy;
        capped = true;
      }
    }
    return score;
  });

  let accuracy = num(pa.AccuracyScore);
  if (capped) {
    // średnia słów liczona z ocen po korekcie — nie wyższa niż to, co podał Azure
    const scored = words.filter((w) => w.error !== 'Insertion');
    if (scored.length > 0) accuracy = Math.min(accuracy, Math.round(scored.reduce((a, w) => a + w.accuracy, 0) / scored.length));
  }

  const result: AssessmentResult = {
    source: 'azure',
    recognizedText: json.DisplayText ?? best.Display,
    completeness: num(pa.CompletenessScore),
    accuracy,
    words,
  };
  if (pa.FluencyScore !== undefined) result.fluency = num(pa.FluencyScore);
  if (pa.ProsodyScore !== undefined) result.prosody = num(pa.ProsodyScore);
  if (pa.PronScore !== undefined) result.pronScore = num(pa.PronScore);
  return result;
}

// --- minimalny interfejs SDK (żeby logikę dało się testować bez prawdziwego pakietu) ----------

export interface SdkRecognitionResult {
  reason: number;
  text?: string;
  errorDetails?: string;
  properties: { getProperty(id: unknown): string };
}
export interface SdkRecognizer {
  recognizeOnceAsync(ok: (r: SdkRecognitionResult) => void, err: (e: string) => void): void;
  close(): void;
}
export interface SdkPaConfig {
  /** W JS SDK to właściwość (setter): `pa.enableProsodyAssessment = true`. W innych wersjach/językach bywa metodą. */
  enableProsodyAssessment?: boolean | (() => void);
  applyTo(recognizer: SdkRecognizer): void;
}
export interface SdkSpeechConfig {
  speechRecognitionLanguage: string;
  outputFormat?: unknown;
  /** Format dźwięku z syntezy (rozmowa z botem: głos Azure Neural). */
  speechSynthesisOutputFormat?: unknown;
  setProperty?(id: unknown, value: string): void;
}
/** Wynik syntezy mowy: gotowy dźwięk (np. mp3) do odtworzenia przez <audio>. */
export interface SdkSynthesisResult {
  reason: number;
  audioData?: ArrayBuffer;
  errorDetails?: string;
}
export interface SdkSynthesizer {
  speakSsmlAsync(ssml: string, ok: (r: SdkSynthesisResult) => void, err: (e: string) => void): void;
  close(): void;
}
/** Strumień „push” SDK: nagrane dane wpuszczamy sami (powtórka rozpoznawania na nagraniu; benchmark). */
export interface SdkPushStream {
  write(data: ArrayBuffer): void;
  close(): void;
}
export interface SdkLike {
  AudioInputStream?: { createPushStream(format?: unknown): SdkPushStream };
  AudioStreamFormat?: { getWaveFormatPCM(rate: number, bits: number, channels: number): unknown };
  SpeechConfig: { fromAuthorizationToken(token: string, region: string): SdkSpeechConfig };
  /** `fromStreamInput` przyjmuje MediaStream (przeglądarka) albo strumień „push” (Node — benchmark). */
  AudioConfig: { fromDefaultMicrophoneInput(): unknown; fromStreamInput?(source: unknown): unknown };
  OutputFormat?: { Detailed: unknown };
  SpeechRecognizer: new (speech: SdkSpeechConfig, audio: unknown) => SdkRecognizer;
  PronunciationAssessmentConfig: new (ref: string, grading: unknown, granularity: unknown, enableMiscue: boolean) => SdkPaConfig;
  PronunciationAssessmentGradingSystem: { HundredMark: unknown };
  PronunciationAssessmentGranularity: { Phoneme: unknown };
  PropertyId: { SpeechServiceResponse_JsonResult: unknown; Speech_SegmentationSilenceTimeoutMs?: unknown };
  ResultReason: { RecognizedSpeech: number; NoMatch: number; Canceled: number };
  /** Synteza mowy (głos bota w rozmowie). `audio: null` = bez odtwarzania przez SDK — dźwięk zwracany jest w wyniku. */
  SpeechSynthesizer?: new (speech: SdkSpeechConfig, audio: unknown) => SdkSynthesizer;
  SpeechSynthesisOutputFormat?: { Audio24Khz48KBitRateMonoMp3: unknown };
}

export type SdkLoader = () => Promise<SdkLike>;

/** Wybiera z zaimportowanego modułu obiekt, który naprawdę ma API SDK (zależnie od bundlera bywa pod `default` albo bezpośrednio). */
export function pickSdk(mod: unknown): SdkLike {
  const candidates = [mod, (mod as { default?: unknown } | null | undefined)?.default];
  for (const c of candidates) {
    const sdk = c as Partial<SdkLike> | null | undefined;
    if (sdk && typeof sdk.SpeechConfig?.fromAuthorizationToken === 'function') return sdk as SdkLike;
  }
  const keys = mod && typeof mod === 'object' ? Object.keys(mod as object).slice(0, 12).join(', ') : typeof mod;
  throw new Error(`Załadowany pakiet SDK nie ma SpeechConfig (klucze modułu: ${keys || 'brak'})`);
}

/** Ładuje SDK dopiero przy pierwszej ocenie (duży pakiet — nie spowalnia startu aplikacji). */
export const defaultSdkLoader: SdkLoader = async () => {
  // @ts-ignore — typy pakietu pojawiają się po `npm install`; używamy własnego, minimalnego SdkLike
  const mod = await import('microsoft-cognitiveservices-speech-sdk');
  return pickSdk(mod);
};

/** Włącza ocenę intonacji (tylko en-US). To dodatek — jej brak albo błąd nie może zepsuć oceny wymowy. */
export function enableProsody(pa: SdkPaConfig): void {
  try {
    if (typeof pa.enableProsodyAssessment === 'function') pa.enableProsodyAssessment();
    else pa.enableProsodyAssessment = true;
  } catch {
    /* ignorujemy */
  }
}

export interface AzureRunArgs {
  sdk: SdkLike;
  token: string;
  region: string;
  reference: string;
  /** Pauza (ms) kończąca wypowiedź — dłuższa niż domyślna, bo dzieci robią przerwy w zdaniu. */
  segmentationSilenceMs?: number;
  /** Źródło dźwięku (AudioConfig). Domyślnie mikrofon przeglądarki; benchmark podaje nagranie. */
  audio?: unknown;
  /** Dostaje surowy JSON odpowiedzi Azure (do zapisu w raporcie benchmarku). */
  onRaw?: (json: string) => void;
  /** Po tylu ms bez odpowiedzi rozpoznawanie jest przerywane błędem (domyślnie 30 s — tyle trwa najdłuższe jednorazowe rozpoznawanie). */
  timeoutMs?: number;
  /** Po tylu ms bez startu sesji (zdarzenie `sessionStarted`) połączenie uznajemy za martwe (domyślnie 8 s; 0 = bez tego limitu). */
  startTimeoutMs?: number;
  /** Diagnostyka: dostaje nazwy zdarzeń SDK (start sesji, wykrycie mowy, wyniki częściowe, anulowanie…). */
  trace?: (event: string) => void;
  /** Sesja rozpoznawania wystartowała (połączenie z usługą gotowe). */
  onStart?: () => void;
}

const TRACE_EVENTS = ['sessionStarted', 'speechStartDetected', 'recognizing', 'recognized', 'speechEndDetected', 'canceled', 'sessionStopped'] as const;

/**
 * Podpina się pod zdarzenia rozpoznawacza: zgłasza je do diagnostyki, a start sesji wyłącza zegar martwego połączenia.
 * Nie wpływa na przebieg samego rozpoznawania.
 */
function attachEvents(rec: SdkRecognizer, trace: ((event: string) => void) | undefined, onStarted: () => void): void {
  const target = rec as unknown as Record<string, unknown>;
  for (const name of TRACE_EVENTS) {
    target[name] = (_sender: unknown, e: { result?: { text?: string; reason?: number }; reason?: number; errorDetails?: string } | undefined) => {
      if (name === 'sessionStarted') onStarted();
      if (!trace) return;
      const detail = name === 'recognizing' || name === 'recognized' ? ` „${e?.result?.text ?? ''}” (reason ${e?.result?.reason ?? '?'})` : name === 'canceled' ? ` reason=${e?.reason ?? '?'} ${e?.errorDetails ?? ''}` : '';
      trace(`${name}${detail}`);
    };
  }
}

/** Rozpoznawanie jednorazowe kończy się samo najpóźniej po ok. 30 s; jeśli SDK w ogóle nie odpowie, nie zostawiamy dziecka z „Słucham…” w nieskończoność. */
export const RECOGNITION_TIMEOUT_MS = 30_000;
/** Tyle czekamy na start sesji rozpoznawania (zwykle poniżej sekundy). Brak startu = martwe połączenie. */
export const START_TIMEOUT_MS = 8_000;

/**
 * Wspólne zakończenie rozpoznawania: raz (kolejne wywołania są ignorowane), zawsze ze zwolnieniem rozpoznawacza
 * i zegara, oraz z limitem czasu na odpowiedź SDK.
 */
function guard(getRecognizer: () => SdkRecognizer | undefined, reject: (e: unknown) => void, timeoutMs: number | undefined, label: string, startTimeoutMs: number | undefined = START_TIMEOUT_MS) {
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let startTimer: ReturnType<typeof setTimeout> | undefined;
  const started = (): void => {
    if (startTimer !== undefined) clearTimeout(startTimer);
    startTimer = undefined;
  };
  const finish = (fn: () => void): void => {
    if (settled) return;
    settled = true;
    if (timer !== undefined) clearTimeout(timer);
    started();
    try {
      getRecognizer()?.close();
    } catch {
      /* ignore */
    }
    fn();
  };
  const arm = (): void => {
    const ms = timeoutMs ?? RECOGNITION_TIMEOUT_MS;
    timer = setTimeout(() => finish(() => reject(new AssessError('network', `Azure nie odpowiedział w ciągu ${Math.round(ms / 1000)} s (${label})`))), ms);
    // martwe połączenie widać po braku startu sesji — nie czekamy wtedy całych 30 s (w razie czego nagranie pozwala powtórzyć rozpoznawanie)
    if (startTimeoutMs > 0 && startTimeoutMs < ms) startTimer = setTimeout(() => finish(() => reject(new AssessError('network', `Azure nie nawiązał połączenia w ciągu ${Math.round(startTimeoutMs / 1000)} s (${label})`))), startTimeoutMs);
  };
  return { finish, arm, started };
}

/** Wspólna konfiguracja rozpoznawania dla obu przejść (z wzorcem i bez). */
function createSpeechConfig(a: { sdk: SdkLike; token: string; region: string; segmentationSilenceMs?: number; lang?: string }, detailed: boolean): SdkSpeechConfig {
  const speech = a.sdk.SpeechConfig.fromAuthorizationToken(a.token, a.region);
  speech.speechRecognitionLanguage = a.lang ?? ASSESS_SETTINGS.lang;
  const seg = a.sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs;
  if (seg !== undefined && speech.setProperty) speech.setProperty(seg, String(a.segmentationSilenceMs ?? ASSESS_SETTINGS.segmentationSilenceMs));
  if (detailed && a.sdk.OutputFormat) speech.outputFormat = a.sdk.OutputFormat.Detailed; // daje też zapis „leksykalny” (słowa bez cyfr i interpunkcji)
  return speech;
}

/** Jedno nasłuchiwanie + ocena (recognizeOnce). */
export function runAzureAssessment(a: AzureRunArgs): Promise<AssessmentResult> {
  const { sdk } = a;
  return new Promise((resolve, reject) => {
    let recognizer: SdkRecognizer | undefined;
    const { finish, arm, started } = guard(() => recognizer, reject, a.timeoutMs, 'ocena wymowy', a.startTimeoutMs);
    try {
      const speech = createSpeechConfig(a, false);
      const audio = a.audio ?? sdk.AudioConfig.fromDefaultMicrophoneInput();
      const pa = new sdk.PronunciationAssessmentConfig(
        a.reference,
        sdk.PronunciationAssessmentGradingSystem.HundredMark,
        sdk.PronunciationAssessmentGranularity.Phoneme,
        ASSESS_SETTINGS.miscue, // enableMiscue: wykrywa pominięte i dodane słowa
      );
      enableProsody(pa);
      recognizer = new sdk.SpeechRecognizer(speech, audio);
      pa.applyTo(recognizer);
      attachEvents(recognizer, a.trace, () => {
        started();
        a.onStart?.();
      });

      arm();
      recognizer.recognizeOnceAsync(
        (r) =>
          finish(() => {
            if (r.reason === sdk.ResultReason.NoMatch) return reject(new AssessError('no-speech'));
            if (r.reason === sdk.ResultReason.Canceled) return reject(new AssessError(classifyError(r.errorDetails), r.errorDetails));
            try {
              const json = r.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult);
              a.onRaw?.(json);
              resolve(parseAzureJson(json));
            } catch (e) {
              reject(e instanceof AssessError ? e : new AssessError('network', e instanceof Error ? e.message : String(e)));
            }
          }),
        (err) => finish(() => reject(new AssessError(classifyError(err), err))),
      );
    } catch (e) {
      // wyjątek podczas konfiguracji (np. brak funkcji w SDK) to problem SDK, nie sieci
      const message = e instanceof Error ? e.message : String(e);
      const code = classifyError(message);
      finish(() => reject(new AssessError(code === 'network' ? 'sdk' : code, message)));
    }
  });
}

export interface PlainRunArgs {
  sdk: SdkLike;
  token: string;
  region: string;
  /** Język rozpoznawania (domyślnie en-US; pytania dziecka w zadaniach zwykle po polsku: pl-PL). */
  lang?: string;
  segmentationSilenceMs?: number;
  audio?: unknown;
  onRaw?: (json: string) => void;
  timeoutMs?: number;
  startTimeoutMs?: number;
  trace?: (event: string) => void;
  onStart?: () => void;
}

interface PlainJson {
  DisplayText?: string;
  NBest?: { Lexical?: string; Display?: string; Confidence?: number }[];
}

/** O ile pewność innej wersji może być niższa od wybranej, żeby uznać ją za „prawie remis”. */
export const TIE_WINDOW = 0.05;

const lexicalOf = (e: { Lexical?: string; Display?: string } | undefined): string => (e?.Lexical || e?.Display || '').trim().toLowerCase();

/**
 * Tekst i pewność ze zwykłego rozpoznawania. Gdy dźwięk jest uciętym słowem („I want foo”), rozpoznawanie dopowiada „food”, ale jest wtedy
 * wyraźnie mniej pewne i podaje kilka prawie równorzędnych wersji („foo”, „fu”, „fuh”…) — tego nie widać w samym tekście.
 */
export function plainDetailsFrom(json: string | undefined, fallback: string): PlainHeard {
  const text = plainTextFrom(json, fallback);
  if (json) {
    try {
      const nbest = (JSON.parse(json) as PlainJson).NBest;
      const best = nbest?.[0];
      if (best && typeof best.Confidence === 'number') {
        const top = lexicalOf(best);
        const ties = (nbest ?? []).slice(1).filter((x) => lexicalOf(x) !== '' && lexicalOf(x) !== top && (x.Confidence ?? 0) >= (best.Confidence as number) - TIE_WINDOW).length;
        const alternatives = (nbest ?? [])
          .slice(0, 4)
          .filter((x) => lexicalOf(x) !== '' && typeof x.Confidence === 'number')
          .map((x) => ({ text: lexicalOf(x), confidence: x.Confidence as number }));
        return { text, confidence: best.Confidence, ties, alternatives };
      }
    } catch {
      /* bez miar pewności — zostaje sam tekst */
    }
  }
  return { text };
}

/** Wyciąga z odpowiedzi „detailed” zapis leksykalny (np. „i have two books”) — nie zależy od zamiany słów na cyfry i interpunkcji. */
export function plainTextFrom(json: string | undefined, fallback: string): string {
  if (json) {
    try {
      const j = JSON.parse(json) as PlainJson;
      const best = j.NBest?.[0];
      const text = best?.Lexical || best?.Display || j.DisplayText;
      if (text) return text;
    } catch {
      /* używamy tekstu z wyniku */
    }
  }
  return fallback;
}

/**
 * Zwykłe rozpoznawanie mowy BEZ zdania wzorcowego i bez oceny wymowy: Azure mówi, co usłyszał, nie „naciągając” tego do wzorca.
 * Cisza (NoMatch) to nie błąd — zwraca pusty tekst.
 */
export function runAzurePlain(a: PlainRunArgs): Promise<string> {
  const { sdk } = a;
  return new Promise((resolve, reject) => {
    let recognizer: SdkRecognizer | undefined;
    const { finish, arm, started } = guard(() => recognizer, reject, a.timeoutMs, 'zwykłe rozpoznawanie', a.startTimeoutMs);
    try {
      const speech = createSpeechConfig(a, true);
      const audio = a.audio ?? sdk.AudioConfig.fromDefaultMicrophoneInput();
      recognizer = new sdk.SpeechRecognizer(speech, audio);
      attachEvents(recognizer, a.trace, () => {
        started();
        a.onStart?.();
      });
      arm();
      recognizer.recognizeOnceAsync(
        (r) =>
          finish(() => {
            if (r.reason === sdk.ResultReason.Canceled) return reject(new AssessError(classifyError(r.errorDetails), r.errorDetails));
            let json: string | undefined;
            try {
              json = r.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult) || undefined;
            } catch {
              /* brak JSON-a — użyjemy r.text */
            }
            if (json) a.onRaw?.(json);
            resolve(r.reason === sdk.ResultReason.NoMatch ? '' : plainTextFrom(json, r.text ?? ''));
          }),
        (err) => finish(() => reject(new AssessError(classifyError(err), err))),
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const code = classifyError(message);
      finish(() => reject(new AssessError(code === 'network' ? 'sdk' : code, message)));
    }
  });
}

export interface DualRunArgs {
  sdk: SdkLike;
  token: string;
  region: string;
  reference: string;
  segmentationSilenceMs?: number;
  /** Fabryka źródeł dźwięku — każde rozpoznawanie dostaje własne (to samo nagranie, osobny strumień). */
  newAudio: () => unknown;
  /**
   * Dotychczasowe nagranie wypowiedzi (16 kHz, 16 bit, mono, przycięte do mowy). Gdy któreś rozpoznawanie zawiesi się albo zgubi połączenie,
   * powtarzamy je na tym nagraniu — dziecko nie musi mówić drugi raz. Brak nagrania = brak powtórek (a więc błąd zamiast oceny).
   */
  recording?: () => Int16Array | null | undefined;
  /** Ile razy powtarzamy nieudane rozpoznawanie na nagraniu (domyślnie 2). */
  replays?: number;
  /** Co ile ms wpuszczamy 100 ms nagrania (domyślnie 100 = tempo mowy; testy dają 0). */
  replayPaceMs?: number;
  onRawScripted?: (json: string) => void;
  onRawPlain?: (json: string) => void;
  timeoutMs?: number;
  startTimeoutMs?: number;
  /** Diagnostyka: dostaje wpisy typu „ocena wymowy: powtórka 1/2 …”. */
  onInfo?: (line: string) => void;
  /** Oba rozpoznawania wystartowały (albo minął `readyMaxMs`) — dziecko może zacząć mówić. */
  onReady?: () => void;
  /** Najdłużej tyle czekamy na start obu sesji, zanim i tak damy znak „mów” (domyślnie 2 s). */
  readyMaxMs?: number;
  /** Po ilu ms od otwarcia mikrofonu zaczęła się mowa (z nagrania) — do diagnostyki ucięcia pierwszego słowa. */
  onsetMs?: () => number | null | undefined;
}

/** Tyle najdłużej czekamy na gotowość obu rozpoznawań, zanim pokażemy „Mów teraz” mimo braku zdarzeń (żeby nie blokować dziecka). */
export const READY_MAX_MS = 2_000;

/** Źródło dźwięku z nagrania: wpuszcza je do strumienia „push” SDK w tempie mowy (jak z mikrofonu) i zamyka strumień. */
export function replaySource(sdk: SdkLike, pcm: Int16Array, paceMs = 100): { audio: unknown; feed: () => Promise<void>; stop: () => void } {
  const create = sdk.AudioInputStream?.createPushStream;
  const format = sdk.AudioStreamFormat?.getWaveFormatPCM;
  const fromStream = sdk.AudioConfig.fromStreamInput;
  if (!create || !format || typeof fromStream !== 'function') throw new AssessError('sdk', 'Ta wersja SDK nie obsługuje strumieni audio (powtórka rozpoznawania na nagraniu jest niemożliwa).');
  const stream = create.call(sdk.AudioInputStream, format.call(sdk.AudioStreamFormat, PCM_RATE, 16, 1));
  const audio = fromStream.call(sdk.AudioConfig, stream);
  const CHUNK = PCM_RATE / 10; // 100 ms
  let stopped = false;
  return {
    audio,
    stop: () => {
      stopped = true;
    },
    feed: async () => {
      try {
        for (let i = 0; i < pcm.length && !stopped; i += CHUNK) {
          const part = pcm.slice(i, i + CHUNK);
          try {
            stream.write(part.buffer.slice(part.byteOffset, part.byteOffset + part.byteLength) as ArrayBuffer);
          } catch {
            break; // rozpoznawanie skończyło się wcześniej i zamknęło strumień
          }
          await new Promise<void>((r) => setTimeout(r, paceMs));
        }
      } finally {
        try {
          stream.close();
        } catch {
          /* już zamknięty */
        }
      }
    },
  };
}

type Which = 'scripted' | 'plain';
const WHICH_PL: Record<Which, string> = { scripted: 'ocena wymowy', plain: 'zwykłe rozpoznawanie' };

/** Zdarzenia SDK w skrócie (nazwy, bez powtórzeń z rzędu) — dopisywane do błędu, żeby było widać, GDZIE połączenie stanęło. */
function trail(events: string[]): string {
  const out: string[] = [];
  for (const e of events) {
    const name = e.replace(/^(\d+ ms )(\S+).*$/, '$1$2');
    if (out[out.length - 1]?.split(' ').pop() !== name.split(' ').pop()) out.push(name);
  }
  return out.length > 0 ? out.slice(-8).join(' → ') : 'żadnych zdarzeń';
}

/**
 * Ocena z bramką treści: dwa rozpoznawania naraz na tym samym nagraniu — (1) ocena wymowy ze zdaniem wzorcowym,
 * (2) zwykły transkrypt bez wzorca — i połączenie wyników (patrz src/lib/gate.ts).
 *
 * Zasady niezawodności:
 * - NIGDY nie oceniamy bez sprawdzonej treści: jeśli któreś z dwóch rozpoznawań nie da odpowiedzi, wynik to błąd, a nie ocena „z tego, co jest”.
 * - Zawieszone lub zerwane połączenie (kod 'network': brak startu sesji, brak odpowiedzi, błąd łączności) nie kończy się od razu błędem:
 *   rozpoznawanie jest powtarzane NA NAGRANIU (po kolei, nie równolegle — jedno połączenie naraz), więc dziecko nie musi mówić jeszcze raz.
 * - cisza w którymkolwiek rozpoznaniu → 'no-speech' (nie liczymy podejścia).
 */
export async function runDualAssessment(a: DualRunArgs): Promise<AssessmentResult> {
  const common = { sdk: a.sdk, token: a.token, region: a.region, ...(a.segmentationSilenceMs !== undefined ? { segmentationSilenceMs: a.segmentationSilenceMs } : {}), ...(a.startTimeoutMs !== undefined ? { startTimeoutMs: a.startTimeoutMs } : {}) };
  const t0 = Date.now();
  const events: Record<Which, string[]> = { scripted: [], plain: [] };
  const tracer = (k: Which) => (e: string): void => void events[k].push(`${Date.now() - t0} ms ${e}`);
  let plainJson: string | undefined;
  let scriptedAudio: unknown;
  let plainAudio: unknown;
  try {
    scriptedAudio = a.newAudio();
    plainAudio = a.newAudio();
  } catch (e) {
    throw e instanceof AssessError ? e : new AssessError('sdk', `Nie udało się podłączyć mikrofonu do rozpoznawania: ${e instanceof Error ? e.message : String(e)}`);
  }

  // „Mów teraz” dopiero, gdy OBA rozpoznawania wystartowały: wcześniej (zaraz po kliknięciu) mikrofon i połączenie jeszcze się włączają, a pierwsze,
  // krótkie słowo („I”) ginie — zrzut z urządzenia: „I want food” → ocena wymowy usłyszała „Want food”, „I” = pominięte
  let startedCount = 0;
  let readyDone = false;
  let readyTimer: ReturnType<typeof setTimeout> | undefined;
  const fireReady = (): void => {
    if (readyDone) return;
    readyDone = true;
    if (readyTimer !== undefined) clearTimeout(readyTimer);
    a.onReady?.();
  };
  const startedOne = (): void => {
    startedCount++;
    if (startedCount >= 2) fireReady();
  };
  if (a.onReady) readyTimer = setTimeout(fireReady, a.readyMaxMs ?? READY_MAX_MS);

  const run = (k: Which, audio: unknown, timeoutMs: number | undefined, first = false): Promise<AssessmentResult | string> =>
    k === 'scripted'
      ? runAzureAssessment({ ...common, reference: a.reference, audio, ...(timeoutMs !== undefined ? { timeoutMs } : {}), ...(a.onRawScripted ? { onRaw: a.onRawScripted } : {}), trace: tracer(k), ...(first ? { onStart: startedOne } : {}) })
      : runAzurePlain({
          ...common,
          audio,
          ...(timeoutMs !== undefined ? { timeoutMs } : {}),
          onRaw: (json) => {
            plainJson = json;
            a.onRawPlain?.(json);
          },
          trace: tracer(k),
          ...(first ? { onStart: startedOne } : {}),
        });

  let replays = 0;
  const outcome: Record<Which, PromiseSettledResult<AssessmentResult | string>> = { scripted: undefined as never, plain: undefined as never };
  [outcome.scripted, outcome.plain] = await Promise.allSettled([run('scripted', scriptedAudio, a.timeoutMs ?? undefined, true), run('plain', plainAudio, a.timeoutMs ?? undefined, true)]);
  readyDone = true; // rozpoznawania skończone — „Mów teraz” nie ma już sensu
  if (readyTimer !== undefined) clearTimeout(readyTimer);

  const codeOf = (k: Which): AssessError['code'] | undefined => {
    const o = outcome[k];
    return o.status === 'rejected' && o.reason instanceof AssessError ? o.reason.code : undefined;
  };
  const failed = (k: Which): boolean => outcome[k].status === 'rejected';
  const retryable = (k: Which): boolean => codeOf(k) === 'network';
  // powtarzamy tylko wtedy, gdy jedyną przeszkodą jest łączność: cisza, brak zgody na mikrofon, błąd tokenu czy SDK powtórką się nie naprawią
  const onlyConnectivity = (['scripted', 'plain'] as const).every((k) => !failed(k) || retryable(k));
  if (onlyConnectivity && (retryable('scripted') || retryable('plain'))) {
    const pcm = a.recording?.() ?? null;
    const maxReplays = a.replays ?? 2;
    if (pcm && pcm.length >= PCM_RATE / 4 && maxReplays > 0) {
      const timeoutMs = a.timeoutMs ?? Math.round(pcm.length / (PCM_RATE / 1000)) + 8_000;
      for (const k of ['scripted', 'plain'] as const) {
        for (let n = 1; n <= maxReplays && retryable(k); n++) {
          const before = outcome[k];
          a.onInfo?.(`${WHICH_PL[k]}: powtórka ${n}/${maxReplays} na nagraniu (${(pcm.length / PCM_RATE).toFixed(1)} s) po błędzie: ${before.status === 'rejected' && before.reason instanceof Error ? before.reason.message : 'nieznany'}`);
          events[k].push(`${Date.now() - t0} ms powtórka-${n}`);
          replays++;
          const src = replaySource(a.sdk, pcm, a.replayPaceMs ?? 100);
          const feeding = src.feed();
          [outcome[k]] = await Promise.allSettled([run(k, src.audio, timeoutMs)]);
          src.stop();
          await feeding;
        }
      }
    }
  }

  const scripted = outcome.scripted;
  const plain = outcome.plain;
  if (scripted.status === 'rejected') throw withTrail(scripted.reason, WHICH_PL.scripted, events.scripted);
  if (plain.status === 'rejected') {
    const reason = plain.reason;
    if (reason instanceof AssessError && reason.code === 'no-speech') throw reason;
    // bez sprawdzonej treści nie wystawiamy oceny — wcześniej tu wracała „łagodna” ocena z notatką, co dawało 3★ za niezrozumiałe słowa
    throw withTrail(reason, WHICH_PL.plain, events.plain);
  }
  if (tokenize(plain.value as string).length === 0) throw new AssessError('no-speech', 'zwykłe rozpoznawanie nie usłyszało słów');
  const combined = combineWithTranscript(a.reference, scripted.value as AssessmentResult, plainDetailsFrom(plainJson, plain.value as string));
  const onset = a.onsetMs?.();
  const out: AssessmentResult = replays > 0 ? { ...combined, replays } : combined;
  return typeof onset === 'number' && Number.isFinite(onset) ? { ...out, speechOnsetMs: Math.round(onset) } : out;
}

/** Dopisuje do błędu, które rozpoznawanie zawiodło i co SDK zdążył zgłosić (kod błędu zostaje bez zmian). */
function withTrail(reason: unknown, label: string, events: string[]): unknown {
  if (!(reason instanceof AssessError) || reason.code === 'no-speech') return reason;
  const err = new AssessError(reason.code, `${label}: ${reason.message} [zdarzenia SDK: ${trail(events)}]`);
  return err;
}

// --- mikrofon przeglądarki jako źródło dźwięku ----------------------------------------------------

export interface MicSession {
  /** Nowe źródło dźwięku dla SDK (każde rozpoznawanie osobne, wszystkie z tego samego mikrofonu). */
  newAudio(): unknown;
  /** Dotychczasowe nagranie (16 kHz mono) przycięte do mowy — do powtórzenia rozpoznawania, gdy połączenie się zawiesi. */
  recording?(): Int16Array | null;
  /** Po ilu ms od otwarcia mikrofonu zaczęła się mowa (null = brak nagrania albo mowy). */
  onsetMs?(): number | null;
  /** Zwalnia mikrofon (wyłącza lampkę nagrywania). */
  release(): void;
}
export type MicOpener = (sdk: SdkLike) => Promise<MicSession>;

/**
 * Otwiera mikrofon jednym getUserMedia i udostępnia go kilku rozpoznawaniom naraz.
 * Każde dostaje własny klon strumienia, więc zamknięcie jednego nie ucina drugiego.
 */
export const openBrowserMic: MicOpener = async (sdk) => {
  const md = (globalThis as { navigator?: { mediaDevices?: { getUserMedia?: (c: unknown) => Promise<MediaStream> } } }).navigator?.mediaDevices;
  if (!md?.getUserMedia) throw new AssessError('unsupported', 'Przeglądarka nie udostępnia mikrofonu (navigator.mediaDevices) — potrzebne HTTPS albo localhost.');
  const fromStream = sdk.AudioConfig.fromStreamInput;
  if (typeof fromStream !== 'function') throw new AssessError('sdk', 'Ta wersja SDK nie obsługuje własnego strumienia audio (AudioConfig.fromStreamInput).');
  let base: MediaStream;
  try {
    base = await md.getUserMedia({ audio: true });
  } catch (e) {
    const name = (e as { name?: string } | null)?.name ?? '';
    const message = e instanceof Error ? `${name ? `${name}: ` : ''}${e.message}` : String(e);
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new AssessError('mic-denied', message);
    throw new AssessError('no-mic', message);
  }
  const clones: MediaStream[] = [];
  const tap = startPcmTap(base); // równoległy zapis dźwięku; gdy się nie uda (null), aplikacja działa bez powtórek
  return {
    newAudio: () => {
      const c = base.clone();
      clones.push(c);
      return fromStream.call(sdk.AudioConfig, c);
    },
    recording: () => (tap ? trimForReplay(tap.snapshot()) : null),
    onsetMs: () => (tap ? speechOnsetMs(tap.snapshot()) : null),
    release: () => {
      tap?.stop();
      for (const st of [base, ...clones]) for (const t of st.getTracks()) t.stop();
    },
  };
};

function classifyError(message: string | undefined): AssessError['code'] {
  const m = (message ?? '').toLowerCase();
  if (m.includes('permission') || m.includes('notallowed') || m.includes('denied') || m.includes('microphone')) return 'mic-denied';
  if (m.includes('401') || m.includes('403') || m.includes('authentication')) return 'token';
  return 'network';
}

// --- token + assessor -------------------------------------------------------------------------

export interface TokenInfo {
  token: string;
  region: string;
}

export interface TokenProvider {
  get(): Promise<TokenInfo>;
}

/**
 * `headers` dostarcza nagłówki uwierzytelniające (klucz urządzenia: `Authorization: Bearer nk_…`) — wołane przy każdym pobraniu tokenu,
 * więc zmiana klucza (parowanie / odłączenie) działa od razu. Serwer odpowiada 401, gdy urządzenie nie jest sparowane lub zostało wyłączone,
 * i 429, gdy skończył się dzienny limit — rozróżniamy je, żeby rodzic dostał właściwą wskazówkę.
 */
export function createTokenProvider(
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  url = './api/token',
  now: () => number = () => Date.now(),
  headers: () => Record<string, string> = () => ({}),
): TokenProvider {
  let cached: (TokenInfo & { expires: number }) | null = null;
  return {
    async get() {
      if (cached && cached.expires > now()) return cached;
      let res: Response;
      try {
        res = await fetchImpl(url, { cache: 'no-store', headers: headers() });
      } catch (e) {
        throw new AssessError('token', e instanceof Error ? e.message : String(e));
      }
      if (res.status === 401) throw new AssessError('unpaired', 'Serwer nie rozpoznaje tego urządzenia (HTTP 401) — rodzic musi je sparować.');
      if (res.status === 429) throw new AssessError('limit', 'Dzienny limit dokładnej oceny dla tego urządzenia jest wyczerpany (HTTP 429).');
      if (!res.ok) {
        let why = '';
        try {
          const body = (await res.json()) as { error?: unknown };
          if (typeof body.error === 'string') why = ` — ${body.error}`; // serwer opisuje, co jest nie tak (brak sekretu, Azure odrzucił klucz…)
        } catch {
          /* odpowiedź bez JSON-u */
        }
        throw new AssessError('token', `HTTP ${res.status}${why}`);
      }
      let body: Partial<TokenInfo>;
      try {
        body = (await res.json()) as Partial<TokenInfo>;
      } catch {
        throw new AssessError('token', 'Odpowiedź /api/token nie jest JSON-em (brak backendu?)');
      }
      if (!body.token || !body.region) throw new AssessError('token', 'Niepełna odpowiedź /api/token');
      cached = { token: body.token, region: body.region, expires: now() + 9 * 60_000 }; // token żyje 10 min
      return cached;
    },
  };
}

/** Ustawienia czasowe oceny (domyślne są dobre; zmieniają je tylko testy i diagnostyka). */
export type AzureAssessorOptions = Pick<DualRunArgs, 'timeoutMs' | 'startTimeoutMs' | 'replays' | 'replayPaceMs' | 'onInfo'>;

export function createAzureAssessor(tokens: TokenProvider, loader: SdkLoader = defaultSdkLoader, openMic: MicOpener = openBrowserMic, options: AzureAssessorOptions = {}): Assessor {
  let sdkPromise: Promise<SdkLike> | null = null;
  return {
    name: 'azure',
    async assess(reference, hooks) {
      const { token, region } = await tokens.get();
      sdkPromise ??= loader();
      let sdk: SdkLike;
      try {
        sdk = await sdkPromise;
      } catch (e) {
        sdkPromise = null;
        throw new AssessError('sdk', e instanceof Error ? e.message : String(e));
      }
      const mic = await openMic(sdk);
      try {
        const recording = mic.recording;
        const onsetMs = mic.onsetMs;
        // surowe odpowiedzi Azure (JSON dokładnie tak, jak zwrócił je SDK) — do zapisu przy nagraniu do strojenia (SPEC §23.40); ostatnia udana odpowiedź każdego z dwóch rozpoznawań
        let scriptedRaw: string | undefined;
        let plainRaw: string | undefined;
        const result = await runDualAssessment({
          sdk,
          token,
          region,
          reference,
          newAudio: () => mic.newAudio(),
          ...(recording ? { recording: () => recording.call(mic) } : {}),
          ...(onsetMs ? { onsetMs: () => onsetMs.call(mic) } : {}),
          ...(hooks?.onReady ? { onReady: hooks.onReady } : {}),
          onRawScripted: (json) => void (scriptedRaw = json),
          onRawPlain: (json) => void (plainRaw = json),
          ...options,
        });
        if (hooks?.onAzureRaw && (scriptedRaw !== undefined || plainRaw !== undefined)) {
          try {
            hooks.onAzureRaw({ ...(scriptedRaw !== undefined ? { scripted: scriptedRaw } : {}), ...(plainRaw !== undefined ? { plain: plainRaw } : {}), ...(result.replays ? { replays: result.replays } : {}) });
          } catch {
            /* zapis do strojenia jest dodatkiem — ocena dziecka ważniejsza */
          }
        }
        // nagranie oddajemy dopiero po udanej ocenie i PRZED zwolnieniem mikrofonu (po `release` zapis dźwięku jest już zatrzymany); błąd w odbiorcy nie psuje oceny
        if (hooks?.onRecording && recording) {
          try {
            const pcm = recording.call(mic);
            if (pcm && pcm.length > 0) hooks.onRecording(pcm);
          } catch {
            /* zapis do strojenia jest dodatkiem — ocena dziecka ważniejsza */
          }
        }
        return result;
      } finally {
        mic.release();
      }
    },
  };
}
